import Alert, { AlertColor } from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import React, { ReactElement, useEffect, useState } from 'react';
import { IconCheckMark24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconCheckMark24px';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import cn from 'classnames';

interface CopyToClipboardProps {
  textToCopy: string;
  icon: ReactElement;
  classNameStyle?: string;
  renderTrigger?: (props: { onClick: () => void }) => ReactElement;
  successMessage?: string;
  autoHideDuration?: number;
  toastStyle?: 'default' | 'custom';
}

type AlertInformation = {
  open: boolean;
  message?: string;
  severity?: AlertColor;
};

const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
  textToCopy,
  icon,
  classNameStyle,
  renderTrigger,
  successMessage = 'Copied to clipboard!',
  autoHideDuration = 6000,
  toastStyle = 'default',
}) => {
  const [alertInformation, setAlertInformation] =
    useState<AlertInformation | null>(null);

  const copyToClipboard = async () => {
    if (!navigator.clipboard) {
      // Clipboard API not available
      return;
    }
    try {
      await navigator.clipboard.writeText(textToCopy);
      setAlertInformation({
        open: true,
        message: successMessage,
        severity: 'success',
      });
    } catch (err) {
      setAlertInformation({
        open: true,
        message: `Failed to copy! ${err}`,
        severity: 'error',
      });
    }
  };

  const handleClose = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setAlertInformation({
      open: false,
    });
  };

  useEffect(() => {
    if (
      !alertInformation?.open ||
      alertInformation.severity !== 'success' ||
      toastStyle !== 'custom'
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setAlertInformation({
        open: false,
      });
    }, autoHideDuration);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    alertInformation?.open,
    alertInformation?.severity,
    autoHideDuration,
    toastStyle,
  ]);

  const shouldUseLinkCopiedToast =
    toastStyle === 'custom' && alertInformation?.severity === 'success';

  return (
    <div className={classNameStyle}>
      {renderTrigger ? (
        renderTrigger({
          onClick: () => {
            void copyToClipboard();
          },
        })
      ) : (
        <Tooltip title="Copy to clipboard" arrow>
          <IconButton onClick={copyToClipboard}>{icon}</IconButton>
        </Tooltip>
      )}
      {alertInformation !== null && (
        <Portal>
          {shouldUseLinkCopiedToast ? (
            <div className="pointer-events-none fixed bottom-6 left-1/2 z-[120] -translate-x-1/2">
              <div
                className={cn(
                  'flex items-center gap-2 rounded-[8px] bg-[#080857] px-4 py-3 shadow-12px',
                )}
              >
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500">
                  <IconCheckMark24pxSvg
                    svg={{ className: 'h-3 w-3 text-white' }}
                  />
                </div>
                <S2Typography
                  translatedContent={alertInformation.message ?? ''}
                  variant="headline-7-alt"
                  textColorVariant="white"
                  dangerouslyOverrideClassNameFn={(className) =>
                    cn(className, 'whitespace-nowrap')
                  }
                />
              </div>
            </div>
          ) : (
            <Snackbar
              open={alertInformation.open}
              autoHideDuration={autoHideDuration}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Alert
                onClose={handleClose}
                severity={alertInformation.severity}
                sx={{ width: '100%' }}
              >
                {alertInformation.message}
              </Alert>
            </Snackbar>
          )}
        </Portal>
      )}
    </div>
  );
};

export default CopyToClipboard;
